import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth } from "../config/firebase";
import { useTheme } from "../context/ThemeContext";
import {
  LayoutDashboard,
  Users,
  Trophy,
  Calendar,
  FileText,
  Settings,
  LogOut,
  Menu,
  X,
  Sun,
  Moon,
  ChevronDown,
  MapPin,
  Award,
  Mail,
  TrendingUp,
} from "lucide-react";

const AdminLayout = ({ children }) => {
  const { isDarkMode, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);

  // Get admin data from session
  const adminData = JSON.parse(sessionStorage.getItem("adminData") || "{}");

  const handleLogout = async () => {
    try {
      await signOut(auth);
      sessionStorage.removeItem("adminData");
      navigate("/admin/login");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const isActiveLink = (path) => {
    return (
      location.pathname === path || location.pathname.startsWith(path + "/")
    );
  };

  const toggleDropdown = (index) => {
    setOpenDropdown(openDropdown === index ? null : index);
  };

  // Navigation items
  const navigationItems = [
    {
      name: "Dashboard",
      icon: LayoutDashboard,
      path: "/admin/dashboard",
    },
    {
      name: "Tournaments",
      icon: Trophy,
      items: [
        { name: "All Tournaments", path: "/admin/tournaments" },
        { name: "Add Tournament", path: "/admin/tournaments/add" },
      ],
    },
    {
      name: "Order of Merit",
      icon: TrendingUp,
      path: "/admin/oom",
    },
    {
      name: "Results",
      icon: Award,
      items: [
        { name: "All Results", path: "/admin/results" },
        { name: "Add Result", path: "/admin/results/add" },
      ],
    },
    {
      name: "Clubs",
      icon: MapPin,
      items: [
        { name: "All Clubs", path: "/admin/clubs" },
        { name: "Add Club", path: "/admin/clubs/add" },
      ],
    },
    {
      name: "News",
      icon: FileText,
      items: [
        { name: "All Posts", path: "/admin/news" },
        { name: "Add Post", path: "/admin/news/add" },
        { name: "Categories", path: "/admin/news/categories" },
      ],
    },
    {
      name: "Admin Users",
      icon: Users,
      items: [
        { name: "Manage Users", path: "/admin/users" },
        { name: "Create New User", path: "/admin/users/create" },
      ],
      adminOnly: true, // Only super_admin and dev can see
    },
  ];

  const NavLink = ({ item, index }) => {
    if (
      item.adminOnly &&
      adminData.role !== "super_admin" &&
      adminData.role !== "dev"
    ) {
      return null;
    }

    // Regular link (no dropdown)
    if (!item.items) {
      const Icon = item.icon;
      return (
        <Link
          to={item.path}
          className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-300 ${
            isActiveLink(item.path)
              ? isDarkMode
                ? "bg-fsgu-green-500/20 text-fsgu-green-400"
                : "bg-fsgu-green-500/10 text-fsgu-green-600"
              : isDarkMode
              ? "text-fsgu-silver-300 hover:bg-fsgu-gray-700"
              : "text-fsgu-silver-700 hover:bg-fsgu-silver-100"
          }`}
          onClick={() => setIsSidebarOpen(false)}
        >
          <Icon className="w-5 h-5 flex-shrink-0" />
          <span className="font-medium">{item.name}</span>
        </Link>
      );
    }

    // Dropdown
    const Icon = item.icon;
    const hasActiveItem = item.items.some((subItem) =>
      isActiveLink(subItem.path)
    );

    return (
      <div>
        <button
          onClick={() => toggleDropdown(index)}
          className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all duration-300 ${
            hasActiveItem || openDropdown === index
              ? isDarkMode
                ? "bg-fsgu-green-500/20 text-fsgu-green-400"
                : "bg-fsgu-green-500/10 text-fsgu-green-600"
              : isDarkMode
              ? "text-fsgu-silver-300 hover:bg-fsgu-gray-700"
              : "text-fsgu-silver-700 hover:bg-fsgu-silver-100"
          }`}
        >
          <div className="flex items-center space-x-3">
            <Icon className="w-5 h-5 flex-shrink-0" />
            <span className="font-medium">{item.name}</span>
          </div>
          <ChevronDown
            className={`w-4 h-4 transition-transform duration-300 ${
              openDropdown === index ? "rotate-180" : ""
            }`}
          />
        </button>

        {openDropdown === index && (
          <div className="mt-1 ml-4 space-y-1">
            {item.items.map((subItem, subIndex) => (
              <Link
                key={subIndex}
                to={subItem.path}
                className={`block px-4 py-2 rounded-lg text-sm transition-all duration-300 ${
                  isActiveLink(subItem.path)
                    ? isDarkMode
                      ? "bg-fsgu-green-500/10 text-fsgu-green-400"
                      : "bg-fsgu-green-500/10 text-fsgu-green-600"
                    : isDarkMode
                    ? "text-fsgu-silver-400 hover:bg-fsgu-gray-700 hover:text-fsgu-silver-300"
                    : "text-fsgu-silver-600 hover:bg-fsgu-silver-100 hover:text-fsgu-silver-900"
                }`}
                onClick={() => setIsSidebarOpen(false)}
              >
                {subItem.name}
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className={`min-h-screen ${
        isDarkMode ? "bg-fsgu-gray-900" : "bg-fsgu-silver-50"
      }`}
    >
      {/* Top Header */}
      <header
        className={`sticky top-0 z-50 border-b ${
          isDarkMode
            ? "bg-fsgu-gray-800 border-fsgu-gray-700"
            : "bg-fsgu-white border-fsgu-silver-200"
        }`}
      >
        <div className="flex items-center justify-between px-4 h-16">
          {/* Left: Mobile menu + Logo */}
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className={`lg:hidden p-2 rounded-lg transition-colors ${
                isDarkMode
                  ? "text-fsgu-silver-300 hover:bg-fsgu-gray-700"
                  : "text-fsgu-silver-700 hover:bg-fsgu-silver-100"
              }`}
            >
              {isSidebarOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>

            <Link to="/admin/dashboard" className="flex items-center space-x-3">
              <img src="/fsgu-logo.png" alt="FSGU" className="h-10 w-auto" />
              <div className="hidden sm:block">
                <h1
                  className={`text-lg font-bold ${
                    isDarkMode ? "text-fsgu-silver-100" : "text-fsgu-silver-900"
                  }`}
                >
                  FSGU Admin
                </h1>
                <p
                  className={`text-xs ${
                    isDarkMode ? "text-fsgu-silver-500" : "text-fsgu-silver-600"
                  }`}
                >
                  Content Management
                </p>
              </div>
            </Link>
          </div>

          {/* Right: Theme toggle + User menu */}
          <div className="flex items-center space-x-3">
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-lg transition-colors ${
                isDarkMode
                  ? "text-fsgu-silver-300 hover:bg-fsgu-gray-700"
                  : "text-fsgu-silver-700 hover:bg-fsgu-silver-100"
              }`}
            >
              {isDarkMode ? (
                <Sun className="w-5 h-5" />
              ) : (
                <Moon className="w-5 h-5" />
              )}
            </button>

            {/* User Info + Logout */}
            <div
              className={`hidden sm:flex items-center space-x-3 px-3 py-2 rounded-lg ${
                adminData.role === "dev"
                  ? isDarkMode
                    ? "bg-gradient-to-r from-fsgu-blue-900/40 to-fsgu-green-900/40 border border-fsgu-blue-500/30"
                    : "bg-gradient-to-r from-fsgu-blue-50 to-fsgu-green-50 border border-fsgu-blue-300/50"
                  : isDarkMode
                  ? "bg-fsgu-gray-700"
                  : "bg-fsgu-silver-100"
              }`}
            >
              <div className="text-right">
                <p
                  className={`text-sm font-medium ${
                    adminData.role === "dev"
                      ? isDarkMode
                        ? "text-fsgu-blue-300"
                        : "text-fsgu-blue-700"
                      : isDarkMode
                      ? "text-fsgu-silver-200"
                      : "text-fsgu-silver-900"
                  }`}
                >
                  {adminData.display_name || "Admin"}
                </p>
                <p
                  className={`text-xs flex items-center gap-1 ${
                    adminData.role === "dev"
                      ? isDarkMode
                        ? "text-fsgu-green-400"
                        : "text-fsgu-green-700"
                      : isDarkMode
                      ? "text-fsgu-silver-500"
                      : "text-fsgu-silver-600"
                  }`}
                >
                  {adminData.role === "dev" && "🔧 "}
                  {adminData.role === "dev"
                    ? "Developer"
                    : adminData.role?.replace("_", " ") || "Administrator"}
                </p>
              </div>
            </div>

            <button
              onClick={handleLogout}
              className={`p-2 rounded-lg transition-all duration-300 hover:scale-105 ${
                isDarkMode
                  ? "text-fsgu-red-400 hover:bg-fsgu-red-900/30"
                  : "text-fsgu-red-600 hover:bg-fsgu-red-50"
              }`}
              title="Logout"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar - Desktop */}
        <aside
          className={`hidden lg:block w-64 min-h-[calc(100vh-4rem)] border-r ${
            isDarkMode
              ? "bg-fsgu-gray-800 border-fsgu-gray-700"
              : "bg-fsgu-white border-fsgu-silver-200"
          }`}
        >
          <nav className="p-4 space-y-2">
            {navigationItems.map((item, index) => (
              <NavLink key={index} item={item} index={index} />
            ))}
          </nav>
        </aside>

        {/* Sidebar - Mobile Overlay */}
        {isSidebarOpen && (
          <>
            <div
              className="lg:hidden fixed inset-0 bg-black/50 z-40"
              onClick={() => setIsSidebarOpen(false)}
            />
            <aside
              className={`lg:hidden fixed left-0 top-16 bottom-0 w-64 z-50 overflow-y-auto ${
                isDarkMode ? "bg-fsgu-gray-800" : "bg-fsgu-white"
              }`}
            >
              <nav className="p-4 space-y-2">
                {navigationItems.map((item, index) => (
                  <NavLink key={index} item={item} index={index} />
                ))}
              </nav>
            </aside>
          </>
        )}

        {/* Main Content */}
        <main className="flex-1 p-4 lg:p-8">{children}</main>
      </div>
    </div>
  );
};

export default AdminLayout;